import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Landing.css';
import logo from '../../assets/wemeetto logo.svg';
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';

function Landing() {
  const featuresRef = useRef(null);
  const aboutRef = useRef(null);
  const contactRef = useRef(null);

  useEffect(() => {

    // Animate on scroll + stagger
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const delay = entry.target.dataset?.delay || 0;
          setTimeout(() => entry.target.classList.add('animate-in'), delay * 80);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -10% 0px' });

    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div className="App">
      <Navbar />

      {/* Hero */}
      <section className="hero">
        {/* Decorative blobs */}
        <div className="blob blob-1" aria-hidden="true" />
        <div className="blob blob-2" aria-hidden="true" />
        <div className="grain" aria-hidden="true" />

        <div className="hero-content">
          <div className="hero-text animate-on-scroll" data-delay="0">
                         <h1 className="hero-title">
               Welcome to <span className="highlight"><img src={logo} alt="Wemeetto" className="hero-logo" /></span>
             </h1>
            <p className="hero-subtitle">
              Connect, chat, and build meaningful relationships in a modern messaging platform.
            </p>
            <div className="hero-buttons">
              <button onClick={() => featuresRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="cta-button primary">
                Explore Features
              </button>
              <button onClick={() => contactRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="cta-button outline">
                Get Started
              </button>
            </div>
            <ul className="trust-badges">
              <li>⚡ Real-time</li>
              <li>🔒 Private</li>
              <li>🎨 Beautiful</li>
            </ul>
          </div>

          {/* Chat preview with animations */}
          <div className="hero-visual animate-on-scroll" data-delay="1" aria-label="Chat preview">
            <div className="chat-phone">
              <div className="chat-header">
                <div className="avatar" />
                <div className="chat-title">
                  <span>Sarah</span>
                  <small className="typing">
                    <span className="dot" /><span className="dot" /><span className="dot" />
                  </small>
                </div>
              </div>

              <div className="chat-body">
                <div className="bubble friend-bubble">
                  <div className="bubble-content">
                    <p>Hey! How's it going? 👋</p>
                    <span className="bubble-time">2:30 PM</span>
                  </div>
                </div>

                <div className="bubble user-bubble wave-in">
                  <div className="bubble-content">
                    <p>Great! Just finished the project 🎉</p>
                    <div className="meta">
                      <span className="bubble-time">2:32 PM</span>
                      {/* <span className="ticks">
                        <i className="tick" /><i className="tick" />
                      </span> */}
                    </div>
                  </div>
                </div>

                <div className="bubble friend-bubble pop-in">
                  <div className="bubble-content">
                    <p>That's awesome! Let's celebrate 🎊</p>
                    <span className="bubble-time">2:33 PM</span>
                  </div>
                </div>
              </div>

              <div className="chat-input">
                <div className="input-pill">
                  <span className="placeholder">Write a message…</span>
                  <button className="send-btn">➤</button>
                </div>
              </div>
            </div>

            <div className="mini-stats">
              <div className="mini-card"><span className="mini-label">Latency</span><span className="mini-value">32ms</span></div>
              <div className="mini-card"><span className="mini-label">Online</span><span className="mini-value">1,204</span></div>
              <div className="mini-card"><span className="mini-label">Uptime</span><span className="mini-value">99.99%</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section ref={featuresRef} id="features" className="features">
        <div className="container">
          <h2 className="section-title animate-on-scroll" data-delay="0">Why Choose wemeetto?</h2>
          <div className="features-grid">
            {[
              ['💬','Real-time Chat','Instant messaging with real-time updates and notifications'],
              ['🔒','Secure & Private','End-to-end encryption for your conversations'],
              ['📱','Cross-platform','Access from anywhere - web, mobile, desktop'],
              ['🎨','Customizable','Personalize your chat experience'],
              ['👥','Group Chats','Create and manage group conversations'],
              ['📎','File Sharing','Share documents, images, and media easily'],
            ].map((f, i) => (
              <article key={i} className="feature-card animate-on-scroll" data-delay={i}>
                <div className="feature-icon">{f[0]}</div>
                <h3>{f[1]}</h3>
                <p>{f[2]}</p>
              </article>
            ))}
          </div>

          <div className="feature-strip animate-on-scroll" data-delay="1">
            <span>Voice Notes</span><span>Typing Status</span><span>Reactions</span><span>Mentions</span><span>Media Previews</span>
          </div>

          <div className="features-cta">
            <button onClick={() => aboutRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="cta-button primary">Learn More</button>
          </div>
        </div>
      </section>

      {/* About */}
      <section ref={aboutRef} id="about" className="about">
        <div className="container">
          <div className="about-content">
            <div className="about-text animate-on-scroll" data-delay="0">
              <h2 className="section-title">About wemeetto</h2>
              <p>wemeetto is more than just a messaging app — it’s a platform designed to bring people together in meaningful ways.</p>
              <p>We obsess over speed, privacy, and delight so your conversations feel natural across every device.</p>
              <div className="stats">
                <div className="stat"><span className="stat-number">10K+</span><span className="stat-label">Active Users</span></div>
                <div className="stat"><span className="stat-number">1M+</span><span className="stat-label">Messages Sent</span></div>
                <div className="stat"><span className="stat-number">50+</span><span className="stat-label">Countries</span></div>
              </div>
            </div>
            <div className="about-visual">
              <div className="about-bubble animate-on-scroll" data-delay="1">
                <p>"wemeetto changed how I stay connected with my team!"</p><span>- Sarah, Designer</span>
              </div>
              <div className="about-bubble animate-on-scroll" data-delay="2">
                <p>"The best messaging app I've ever used!"</p><span>- Mike, Developer</span>
              </div>
              <div className="about-bubble animate-on-scroll" data-delay="3">
                <p>"Simple, beautiful, and powerful!"</p><span>- Emma, Manager</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section ref={contactRef} id="contact" className="contact">
        <div className="container">
          <h2 className="section-title animate-on-scroll" data-delay="0">Get in Touch</h2>
          <div className="contact-content">
            <div className="contact-info">
              <div className="contact-item animate-on-scroll" data-delay="1">
                <div className="contact-icon">📧</div>
                <div><h3>Email</h3><p>hello@wemeetto.com</p></div>
              </div>
              <div className="contact-item animate-on-scroll" data-delay="2">
                <div className="contact-icon">🌐</div>
                <div><h3>Website</h3><p>www.wemeetto.com</p></div>
              </div>
            </div>

            <div className="contact-form animate-on-scroll" data-delay="1">
              <form onSubmit={(e)=>e.preventDefault()}>
                <div className="form-group"><input type="text" placeholder="Your Name" className="form-input" /></div>
                <div className="form-group"><input type="email" placeholder="Your Email" className="form-input" /></div>
                <div className="form-group"><textarea placeholder="Your Message" className="form-textarea" rows="4" /></div>
                <button type="submit" className="cta-button primary">Send Message</button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Landing;
